// Rollover chain rail — 5-week visual timeline.
//
// Renders one cell per week up to chain.total_rollovers (default 5).
// Past cells are filled from Phantom history (terminal cycles).
// The active cell is the latest non-terminal Phantom cycle.
// Future cells are placeholders — never invented data.
//
// Styling: FpKit (Football Products) Kalshi-clean design system —
// inline styles over CSS variables. Renders inside the `.fp` scope
// (see RolloverPage), so `--fp-*` tokens resolve. Legacy rollover.css
// classes (.chain-rail, .crh-head, .rail-*) are no longer used here.

import type { CSSProperties } from "react";
import { Pill } from "../../components/super-acca/Pill.tsx";
import {
  formatWindowConfig,
  WINDOW_MODES,
  type WindowMode,
} from "../../rollover/windowDisplay.ts";
import type { RolloverChainRow } from "../../vault/useRolloverChain.ts";
import type { PhantomCycleRow, PhantomSelection } from "../../phantom/usePhantomCycle.ts";

export interface RolloverChainRailProps {
  readonly chain: RolloverChainRow | null;
  readonly activeCycle: PhantomCycleRow | null;
  readonly history: readonly PhantomCycleRow[];
  readonly cumulativeProtected: number | null;
}

interface RailWeek {
  n: number;
  state: "done" | "active" | "future";
  amt: number | null;
  when: string;
  pickName: string;
  pickRes: string;
}

function firstSelectionLabel(selections: PhantomCycleRow["selections"]): string {
  if (Array.isArray(selections) && selections.length > 0) {
    const s = selections[0] as PhantomSelection;
    if (s?.phantom_pick) return s.phantom_pick;
    if (s?.home_team && s?.away_team) return `${s.home_team} v ${s.away_team}`;
  }
  return "—";
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtCcy(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `GH₵${Math.round(n).toLocaleString()}`;
}

function weekState(cycle: PhantomCycleRow): "done" | "active" {
  return cycle.status === "phantom_won" || cycle.status === "phantom_busted" || cycle.status === "expired"
    ? "done"
    : "active";
}

// ── Shared inline-style fragments (Kalshi-clean tokens) ──────────
const MONO_LABEL: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
};

const DISPLAY: CSSProperties = {
  fontFamily: "'Bebas Neue', sans-serif",
  fontWeight: 400,
  color: "var(--text)",
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
};

const DOT: CSSProperties = {
  width: 3,
  height: 3,
  borderRadius: "50%",
  background: "var(--text-quaternary)",
  display: "inline-block",
  margin: "0 7px",
  verticalAlign: "middle",
};

export function RolloverChainRail({
  chain,
  activeCycle,
  history,
  cumulativeProtected,
}: RolloverChainRailProps): JSX.Element {
  const totalWeeks = chain?.total_rollovers ?? 5;
  const oddsTarget = chain?.odds_target ?? null;

  // Phase RV-4a — window config chip. Defaults match the DB default
  // (`weekend_only`) so legacy chains (pre-RV-4a) render correctly.
  const windowModeRaw = chain?.window_config;
  const windowMode: WindowMode = (
    typeof windowModeRaw === "string" &&
    (WINDOW_MODES as readonly string[]).includes(windowModeRaw)
      ? windowModeRaw
      : "weekend_only"
  ) as WindowMode;
  const customDays = chain?.custom_days ?? null;
  const windowChipLabel = formatWindowConfig(windowMode, customDays);

  // Build a map of week_number → cycle
  const cyclesByWeek = new Map<number, PhantomCycleRow>();
  for (const c of history) cyclesByWeek.set(c.week_number, c);
  if (activeCycle) cyclesByWeek.set(activeCycle.week_number, activeCycle);

  const weeks: RailWeek[] = [];
  for (let n = 1; n <= totalWeeks; n++) {
    const cycle = cyclesByWeek.get(n);
    if (!cycle) {
      weeks.push({
        n,
        state: "future",
        amt: null,
        when: "Pending",
        pickName: "—",
        pickRes: "",
      });
      continue;
    }
    const isTerminal =
      cycle.status === "phantom_won" ||
      cycle.status === "phantom_busted" ||
      cycle.status === "expired";
    const result = isTerminal
      ? cycle.status === "phantom_won"
        ? "won"
        : cycle.status === "phantom_busted"
          ? "lost"
          : "expired"
      : "open";
    const dateStr = shortDate(cycle.created_at);
    const payout = (cycle.payout ?? null) as number | null;
    const profit =
      payout !== null && cycle.stake_amount !== null
        ? payout - cycle.stake_amount
        : null;
    weeks.push({
      n,
      state: weekState(cycle),
      amt: cycle.stake_amount,
      when: `${dateStr}${result !== "open" ? ` · ${result}` : ""}`,
      pickName: firstSelectionLabel(cycle.selections),
      pickRes:
        isTerminal && cycle.status === "phantom_won" && profit !== null
          ? `+GH₵${Math.round(profit)}${oddsTarget ? ` · ${oddsTarget.toFixed(2)}` : ""}`
          : !isTerminal
            ? `Active${oddsTarget ? ` · ${oddsTarget.toFixed(2)}` : ""}`
            : "—",
    });
  }

  // Phase RV-UI / GAP 4 — Wins recorded counter must count only
  // resolved-WON weeks for the CURRENT chain. The history feed is already
  // scoped to `chain.id` by usePhantomCycleHistory, so we filter by the
  // phantom 'won' status on that scoped set. We deliberately do NOT count
  // cells whose only signal is "rail filled" (busted/expired are
  // terminal but NOT wins). Chain seed events have no phantom status and
  // never enter `history`, so they are excluded by construction.
  const wins = chain
    ? history.filter(
        (c) => c.chain_id === chain.id && c.status === "phantom_won",
      ).length
    : 0;
  const filledPct = totalWeeks > 0 ? Math.min(100, Math.round((wins / totalWeeks) * 100)) : 0;

  const opening = chain?.opening_stake ?? null;
  const current = chain?.current_value ?? null;
  const projected =
    opening !== null && oddsTarget !== null
      ? Math.round(opening * Math.pow(oddsTarget, totalWeeks))
      : null;

  const startedAt = (() => {
    const earliest = [...history, ...(activeCycle ? [activeCycle] : [])].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    )[0];
    return earliest ? shortDate(earliest.created_at) : null;
  })();

  return (
    <div
      style={{
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        overflow: "hidden",
        marginBottom: 12,
      }}
    >
      {/* ── Header ─────────────────────────────────────── */}
      <div
        style={{
          padding: "14px 16px 12px",
          borderBottom: "1px solid var(--border-faint)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ ...MONO_LABEL, color: "var(--fp-accent-text)", letterSpacing: "0.14em" }}>
            {totalWeeks}-WEEK ROLLOVER
            {chain ? ` · PLAN #${chain.id.slice(-6).toUpperCase()}` : ""}
          </div>
          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.03em",
              color: "var(--text-tertiary)",
            }}
          >
            <span data-test="window-config-chip" style={{ marginRight: 8 }}>
              <Pill>{windowChipLabel}</Pill>
            </span>
            {startedAt ? <>Started {startedAt}</> : null}
            {projected !== null ? (
              <>
                <span style={DOT} />
                Target GH₵{projected.toLocaleString()}
              </>
            ) : null}
            {oddsTarget !== null ? (
              <>
                <span style={DOT} />
                {oddsTarget.toFixed(2)}× weekly
              </>
            ) : null}
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
          {([
            ["Won", "var(--fp-won)"],
            ["Active", "var(--fp-accent)"],
            ["Future", "var(--text-quaternary)"],
          ] as const).map(([label, color]) => (
            <span
              key={label}
              style={{ ...MONO_LABEL, display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Rail body ───────────────────────────────────── */}
      <div style={{ padding: "18px 16px 14px" }}>
        {/* Progress track */}
        <div
          style={{
            position: "relative",
            height: 3,
            borderRadius: 2,
            background: "var(--bg-inset)",
            marginBottom: 18,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${filledPct}%`,
              background: "var(--fp-accent)",
              borderRadius: 2,
              transition: "width .4s ease",
            }}
          />
        </div>

        {/* Week cells */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${totalWeeks}, 1fr)`,
            gap: 10,
          }}
        >
          {weeks.map((w) => {
            const tint =
              w.state === "done"
                ? { bg: "var(--fp-won-soft)", fg: "var(--fp-won)", bd: "var(--fp-won)" }
                : w.state === "active"
                  ? { bg: "var(--fp-accent-soft)", fg: "var(--fp-accent-text)", bd: "var(--fp-accent)" }
                  : { bg: "var(--bg-inset)", fg: "var(--text-quaternary)", bd: "var(--border-faint)" };
            return (
              <div
                key={w.n}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  minWidth: 0,
                }}
              >
                {/* Node badge */}
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    background: tint.bg,
                    border: `1px solid ${tint.bd}`,
                    color: tint.fg,
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                  }}
                >
                  {w.state === "done" ? "✓" : w.n}
                </div>

                <div style={{ ...MONO_LABEL, marginTop: 8 }}>Week {w.n}</div>

                <div style={{ ...DISPLAY, fontSize: 20, marginTop: 4 }}>
                  {w.amt !== null ? fmtCcy(w.amt) : "—"}
                </div>

                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9,
                    letterSpacing: "0.02em",
                    color: "var(--text-quaternary)",
                    marginTop: 3,
                    textTransform: "capitalize",
                  }}
                >
                  {w.when}
                </div>

                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: "1px solid var(--border-faint)",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: "var(--text-secondary)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={w.pickName}
                  >
                    {w.pickName}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      fontWeight: 600,
                      color:
                        w.state === "done"
                          ? "var(--fp-won)"
                          : w.state === "active"
                            ? "var(--fp-accent-text)"
                            : "var(--text-tertiary)",
                    }}
                  >
                    {w.pickRes || "—"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Footer stats ────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${totalWeeks}, 1fr)`,
          borderTop: "1px solid var(--border-faint)",
          background: "var(--bg-inset)",
        }}
      >
        {([
          { k: "Starting stake", v: fmtCcy(opening), tone: "default" as const },
          { k: "Compounded so far", v: fmtCcy(current), tone: "pos" as const },
          { k: "Protected to date", v: fmtCcy(cumulativeProtected), tone: "default" as const },
          {
            k: `Projected at Week ${totalWeeks}`,
            v: projected !== null ? `GH₵${projected.toLocaleString()}` : "—",
            tone: "default" as const,
          },
          { k: "Wins recorded", v: `${wins} / ${totalWeeks}`, tone: "default" as const },
        ]).map((col, i) => (
          <div
            key={col.k}
            style={{
              padding: "12px 14px",
              borderLeft: i === 0 ? "none" : "1px solid var(--border-faint)",
              minWidth: 0,
            }}
          >
            <div style={MONO_LABEL}>{col.k}</div>
            <div
              style={{
                ...DISPLAY,
                fontSize: 22,
                marginTop: 4,
                color: col.tone === "pos" ? "var(--fp-won)" : "var(--text)",
              }}
            >
              {col.v}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
